import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import VideoEmbed from '../blog/components/VideoEmbed';
import ContentRenderer from '../article/components/ContentRenderer';
import RichTextEditor from './components/RichTextEditor';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  author_avatar: string;
  image: string;
  header_image: string | null;
  video_url: string | null;
  video_type: string | null;
  reading_time: number;
  published_date: string;
  created_at: string;
  updated_at: string;
  status: 'draft' | 'published';
  sort_order: number;
  faqs?: FAQItem[];
  keywords?: string[];
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  canonical_url?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SortableRowProps {
  post: BlogPost;
  onEdit: (post: BlogPost) => void;
  onDuplicate: (post: BlogPost) => void;
  onToggleStatus: (post: BlogPost) => void;
  onDelete: (id: string) => void;
  formatDate: (date: string) => string;
}

function SortableRow({ post, onEdit, onDuplicate, onToggleStatus, onDelete, formatDate }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b bg-white hover:bg-gray-50">
      <td className="px-4 py-2 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-draggable text-lg"></i>
          </div>
        </button>
      </td>
      <td className="px-4 py-2">{post.title}</td>
      <td className="px-4 py-2">
        <span className={`px-2 py-1 text-xs rounded-full ${
          post.status === 'published' 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {post.status === 'published' ? '公開中' : '下書き'}
        </span>
      </td>
      <td className="px-4 py-2 text-gray-500 text-sm">{formatDate(post.created_at)}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => onEdit(post)} 
            className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
          >
            編集
          </button>
          <button 
            onClick={() => onDuplicate(post)} 
            className="text-purple-600 hover:text-purple-800 text-sm cursor-pointer"
          >
            複製
          </button>
          <button 
            onClick={() => onToggleStatus(post)} 
            className="text-green-600 hover:text-green-800 text-sm cursor-pointer"
          >
            {post.status === 'published' ? '下書きに戻す' : '公開'}
          </button>
          <button
            onClick={() => onDelete(post.id)}
            className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
          >
            削除
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string; link?: string; linkText?: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [contentUploading, setContentUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [isSortMode, setIsSortMode] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    author_avatar: '',
    image: '',
    header_image: '',
    video_url: '',
    video_type: '',
    reading_time: 5,
    status: 'draft' as 'draft' | 'published',
    meta_title: '',
    meta_description: '',
    og_image: '',
    faqs: [] as FAQItem[],
    keywords: [] as string[],
    canonical_url: '',
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('published_date', { ascending: false });

    if (error) {
      setNotification({ type: 'error', message: '記事の取得に失敗しました' });
    } else {
      setPosts(data || []);
    }
    setLoading(false);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredPosts.findIndex((post) => post.id === active.id);
      const newIndex = filteredPosts.findIndex((post) => post.id === over.id);

      const newOrder = arrayMove(filteredPosts, oldIndex, newIndex);
      
      // Update local state immediately for smooth UX
      if (statusFilter === 'all') {
        setPosts(newOrder);
      } else {
        // If filtered, we need to update the full posts array
        const updatedPosts = [...posts];
        const filteredIds = newOrder.map(p => p.id);
        let filterIndex = 0;
        for (let i = 0; i < updatedPosts.length; i++) {
          if (filteredIds.includes(updatedPosts[i].id)) {
            updatedPosts[i] = newOrder[filterIndex];
            filterIndex++;
          }
        }
        setPosts(updatedPosts);
      }

      // Save new order to database
      try {
        const updates = newOrder.map((post, index) => ({
          id: post.id,
          sort_order: index,
        }));

        for (const update of updates) {
          await supabase
            .from('blog_posts')
            .update({ sort_order: update.sort_order })
            .eq('id', update.id);
        }

        setNotification({ type: 'success', message: '並び順を保存しました' });
      } catch (error) {
        setNotification({ type: 'error', message: '並び順の保存に失敗しました' });
        fetchPosts(); // Revert on error
      }
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setNotification({ type: 'error', message: '画像サイズは5MB以下にしてください' });
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setNotification({ type: 'error', message: 'JPEG、PNG、GIF、WebP形式の画像のみアップロードできます' });
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `blog/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image: publicUrl });
      setNotification({ type: 'success', message: '画像をアップロードしました' });
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({ type: 'error', message: '画像のアップロードに失敗しました' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingPost(null);
    setActiveTab('edit');
    setKeywordInput('');
    setEditorKey(prev => prev + 1);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      author: '',
      author_avatar: '',
      image: '',
      header_image: '',
      video_url: '',
      video_type: '',
      reading_time: 5,
      status: 'draft',
      meta_title: '',
      meta_description: '',
      og_image: '',
      faqs: [],
      keywords: [],
      canonical_url: '',
    });
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setIsCreating(false);
    setActiveTab('edit');
    setKeywordInput('');
    setEditorKey(prev => prev + 1);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      author_avatar: post.author_avatar || '',
      image: post.image,
      header_image: post.header_image || '',
      video_url: post.video_url || '',
      video_type: post.video_type || '',
      reading_time: post.reading_time,
      status: post.status || 'draft',
      meta_title: (post as any).meta_title || '',
      meta_description: (post as any).meta_description || '',
      og_image: (post as any).og_image || '',
      faqs: (post as any).faqs || [],
      keywords: (post as any).keywords || [],
      canonical_url: (post as any).canonical_url || '',
    });
  };

  const handleCancel = () => {
    setEditingPost(null);
    setIsCreating(false);
    setActiveTab('edit');
  };

  const handleSave = async (saveStatus: 'draft' | 'published') => {
    if (!formData.title || !formData.slug || !formData.content) {
      setNotification({ type: 'error', message: 'タイトル、スラッグ、本文は必須です' });
      return;
    }

    setSaving(true);

    const postData = {
      title: formData.title,
      slug: formData.slug,
      excerpt: formData.excerpt,
      content: formData.content,
      category: formData.category,
      author: formData.author,
      author_avatar: formData.author_avatar || null,
      image: formData.image,
      header_image: formData.header_image || null,
      video_url: formData.video_url || null,
      video_type: formData.video_type || null,
      reading_time: formData.reading_time,
      status: saveStatus,
      meta_title: formData.meta_title || null,
      meta_description: formData.meta_description || null,
      og_image: formData.og_image || null,
      faqs: formData.faqs.length > 0 ? formData.faqs : null,
      keywords: formData.keywords.length > 0 ? formData.keywords : null,
      canonical_url: formData.canonical_url || null,
    };

    if (isCreating) {
      const { error } = await supabase.from('blog_posts').insert({
        ...postData,
        published_date: saveStatus === 'published' ? new Date().toISOString() : null,
      });
      if (error) {
        setNotification({ type: 'error', message: '記事の作成に失敗しました' });
      } else {
        if (saveStatus === 'published') {
          setNotification({ 
            type: 'success', 
            message: '記事を公開しました！', 
            link: `/blog/${formData.slug}`,
            linkText: '公開記事を見る'
          });
        } else {
          setNotification({ type: 'success', message: '下書きを保存しました' });
        }
        setIsCreating(false);
        fetchPosts();
      }
    } else if (editingPost) {
      const updateData: any = {
        ...postData,
        updated_at: new Date().toISOString(),
      };
      if (saveStatus === 'published' && editingPost.status === 'draft') {
        updateData.published_date = new Date().toISOString();
      }
      const { error } = await supabase.from('blog_posts').update(updateData).eq('id', editingPost.id);
      if (error) {
        setNotification({ type: 'error', message: '記事の更新に失敗しました' });
      } else {
        if (saveStatus === 'published') {
          setNotification({ 
            type: 'success', 
            message: '記事を公開しました！', 
            link: `/blog/${formData.slug}`,
            linkText: '公開記事を見る'
          });
        } else {
          setNotification({ type: 'success', message: '下書きを保存しました' });
        }
        setEditingPost(null);
        fetchPosts();
      }
    }
    setSaving(false);
  };

  const handleToggleStatus = async (post: BlogPost) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    const updateData: any = {
      status: newStatus,
      updated_at: new Date().toISOString(),
    };
    if (newStatus === 'published' && !post.published_date) {
      updateData.published_date = new Date().toISOString();
    }
    const { error } = await supabase.from('blog_posts').update(updateData).eq('id', post.id);
    if (error) {
      setNotification({ type: 'error', message: 'ステータスの変更に失敗しました' });
    } else {
      if (newStatus === 'published') {
        setNotification({ 
          type: 'success', 
          message: '記事を公開しました！', 
          link: `/blog/${post.slug}`,
          linkText: '公開記事を見る'
        });
      } else {
        setNotification({ type: 'success', message: '記事を下書きに戻しました' });
      }
      fetchPosts();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (error) {
      setNotification({ type: 'error', message: '記事の削除に失敗しました' });
    } else {
      setNotification({ type: 'success', message: '記事を削除しました' });
      setDeleteConfirm(null);
      fetchPosts();
    }
  };

  const handleDuplicate = async (post: BlogPost) => {
    const duplicatedPost = {
      title: `${post.title}（コピー）`,
      slug: `${post.slug}-copy-${Date.now()}`,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      author_avatar: post.author_avatar || null,
      image: post.image,
      header_image: post.header_image || null,
      video_url: post.video_url || null,
      video_type: post.video_type || null,
      reading_time: post.reading_time,
      status: 'draft' as const,
      meta_title: (post as any).meta_title || null,
      meta_description: (post as any).meta_description || null,
      og_image: (post as any).og_image || null,
      faqs: (post as any).faqs || null,
      keywords: (post as any).keywords || null,
      canonical_url: null,
      published_date: null,
    };

    const { error } = await supabase.from('blog_posts').insert(duplicatedPost);
    if (error) {
      setNotification({ type: 'error', message: '記事の複製に失敗しました' });
    } else {
      setNotification({ type: 'success', message: '記事を複製しました（下書きとして保存）' });
      fetchPosts();
    }
  };

  const handleContentImageUpload = async (file: File): Promise<string | null> => {
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ type: 'error', message: '画像サイズは5MB以下にしてください' });
      return null;
    }
    if (!allowedTypes.includes(file.type)) {
      setNotification({ type: 'error', message: 'JPEG、PNG、GIF、WebP形式のみ' });
      return null;
    }

    setContentUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `blog/content/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('blog-images').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('blog-images').getPublicUrl(filePath);
      setNotification({ type: 'success', message: '画像を挿入しました' });
      return publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({ type: 'error', message: '画像のアップロードに失敗しました' });
      return null;
    } finally {
      setContentUploading(false);
    }
  };

  const formatPreviewDate = (dateString?: string) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');
  };

  const addFAQ = () => {
    setFormData({ ...formData, faqs: [...formData.faqs, { question: '', answer: '' }] });
  };

  const updateFAQ = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...formData.faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    setFormData({ ...formData, faqs: newFaqs });
  };

  const removeFAQ = (index: number) => {
    setFormData({ ...formData, faqs: formData.faqs.filter((_, i) => i !== index) });
  };

  const moveFAQ = (index: number, direction: 'up' | 'down') => {
    const newFaqs = [...formData.faqs];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFaqs.length) return;
    [newFaqs[index], newFaqs[newIndex]] = [newFaqs[newIndex], newFaqs[index]];
    setFormData({ ...formData, faqs: newFaqs });
  };

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formData.keywords.includes(trimmed) && formData.keywords.length < 10) {
      setFormData({ ...formData, keywords: [...formData.keywords, trimmed] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData({ ...formData, keywords: formData.keywords.filter(k => k !== keyword) });
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === '\u3001') {
      e.preventDefault();
      addKeyword();
    }
  };

  const calculateReadingTime = (html: string): number => {
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, '');
    const charCount = text.length;
    const minutes = Math.ceil(charCount / 500);
    return Math.max(1, minutes);
  };

  const categories = ['配信機材', '配信ソフト', 'トラブル対処', '配信テクニック', '初心者向け', 'プロ向け'];

  const authors = [
    {
      name: '配信技術部 高橋',
      avatar: 'https://public.readdy.ai/ai/img_res/19936f294127a6e47945448bcef70b78.jpg',
    },
    {
      name: '機材管理部 中村',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20japanese%20female%20technician%20in%20her%2020s%20wearing%20casual%20business%20attire%20clean%20simple%20white%20background%20portrait%20photo%20natural%20lighting%20confident%20expression&width=200&height=200&seq=author2&orientation=squarish',
    },
    {
      name: 'コンテンツ制作部 小林',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20japanese%20male%20content%20creator%20in%20his%2030s%20wearing%20casual%20business%20attire%20clean%20simple%20white%20background%20portrait%20photo%20natural%20lighting%20creative%20look&width=200&height=200&seq=author3&orientation=squarish',
    },
    {
      name: 'トラブル対応部 加藤',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20japanese%20female%20support%20specialist%20in%20her%2020s%20wearing%20casual%20business%20attire%20clean%20simple%20white%20background%20portrait%20photo%20natural%20lighting%20helpful%20smile&width=200&height=200&seq=author4&orientation=squarish',
    },
    {
      name: '配信企画部 渡辺',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20japanese%20male%20planner%20in%20his%2030s%20wearing%20casual%20business%20attire%20clean%20simple%20white%20background%20portrait%20photo%20natural%20lighting%20professional%20appearance&width=200&height=200&seq=author5&orientation=squarish',
    },
  ];

  const handleAuthorChange = (authorName: string) => {
    const selectedAuthor = authors.find(a => a.name === authorName);
    setFormData({
      ...formData,
      author: authorName,
      author_avatar: selectedAuthor?.avatar || '',
    });
  };

  const filteredPosts = posts.filter(post => {
    if (statusFilter === 'all') return true;
    return post.status === statusFilter;
  });

  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl"></i>
              </div>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">記事管理</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSortMode(!isSortMode)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                isSortMode 
                  ? 'bg-teal-600 text-white' 
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-drag-move-line"></i>
              </div>
              {isSortMode ? '並び替え中' : '並び替え'}
            </button>
            <Link
              to="/robots"
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-robot-line"></i>
              </div>
              robots.txt
            </Link>
            <Link
              to="/sitemap"
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sitemap-line"></i>
              </div>
              サイトマップ
            </Link>
            <Link
              to="/admin/categories"
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-folder-settings-line"></i>
              </div>
              カテゴリー設定
            </Link>
            <Link
              to="/admin/authors"
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-settings-line"></i>
              </div>
              著者設定
            </Link>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
              新規作成
            </button>
          </div>
        </div>
      </header>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-xl max-w-md ${
            notification.type === 'success' ? 'bg-teal-600 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <i className={`${notification.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                <span className="font-semibold">{notification.message}</span>
              </div>
              {notification.link && notification.linkText && (
                <Link
                  to={notification.link}
                  className="inline-flex items-center gap-1 text-sm underline hover:no-underline mt-2"
                  onClick={() => setNotification(null)}
                >
                  {notification.linkText}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Editor Modal */}
        {(isCreating || editingPost) && (
          <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-6">
                  <h2 className="text-lg font-bold text-gray-900">{isCreating ? '新規記事作成' : '記事編集'}</h2>

                  {/* Tab Switcher */}
                  <div className="flex items-center bg-gray-100 rounded-full p-1">
                    <button
                      onClick={() => setActiveTab('edit')}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'edit' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-edit-line mr-1.5"></i>編集
                    </button>
                    <button
                      onClick={() => setActiveTab('preview')}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-eye-line mr-1.5"></i>プレビュー
                    </button>
                  </div>
                </div>

                <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="p-6">
                {/* Content */}
                {activeTab === 'edit' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">タイトル</label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">スラッグ</label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={formData.slug}
                        onChange={e => setFormData({ ...formData, slug: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">抜粋</label>
                      <textarea
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        rows={3}
                        value={formData.excerpt}
                        onChange={e => setFormData({ ...formData, excerpt: e.target.value })}
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">カテゴリ</label>
                      <select
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={formData.category}
                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                      >
                        <option value="">選択してください</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">著者</label>
                      <select
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={formData.author}
                        onChange={e => handleAuthorChange(e.target.value)}
                      >
                        <option value="">選択してください</option>
                        {authors.map(a => (
                          <option key={a.name} value={a.name}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">サムネイル画像（1200×630px推奨）</label>
                      {formData.image ? (
                        <div className="relative inline-block">
                          <img src={formData.image} alt="サムネイル" className="h-32 w-56 object-cover rounded-lg border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, image: '' })}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 cursor-pointer"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-56 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={uploading}
                            className="hidden"
                          />
                          {uploading ? (
                            <div className="flex items-center gap-2 text-gray-500">
                              <i className="ri-loader-4-line animate-spin"></i>
                              <span className="text-sm">アップロード中...</span>
                            </div>
                          ) : (
                            <>
                              <div className="w-8 h-8 flex items-center justify-center text-gray-400 mb-1">
                                <i className="ri-image-add-line text-2xl"></i>
                              </div>
                              <span className="text-sm text-gray-500">クリックして画像を選択</span>
                              <span className="text-xs text-gray-400 mt-1">記事一覧に表示されます</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ヘッダー画像（1920×1080px推奨）</label>
                      {formData.header_image ? (
                        <div className="relative inline-block">
                          <img src={formData.header_image} alt="ヘッダー" className="h-32 w-64 object-cover rounded-lg border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, header_image: '' })}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 cursor-pointer"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-64 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                setNotification({ type: 'error', message: '画像サイズは5MB以下にしてください' });
                                return;
                              }
                              if (!allowedTypes.includes(file.type)) {
                                setNotification({ type: 'error', message: 'JPEG、PNG、GIF、WebP形式の画像のみアップロードできます' });
                                return;
                              }
                              setUploadingHeader(true);
                              try {
                                const fileExt = file.name.split('.').pop();
                                const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
                                const filePath = `blog/headers/${fileName}`;
                                const { error: uploadError } = await supabase.storage.from('blog-images').upload(filePath, file);
                                if (uploadError) throw uploadError;
                                const { data: { publicUrl } } = supabase.storage.from('blog-images').getPublicUrl(filePath);
                                setFormData(prev => ({ ...prev, header_image: publicUrl }));
                                setNotification({ type: 'success', message: 'ヘッダー画像をアップロードしました' });
                              } catch (error) {
                                console.error('Upload error:', error);
                                setNotification({ type: 'error', message: '画像のアップロードに失敗しました' });
                              } finally {
                                setUploadingHeader(false);
                                e.target.value = '';
                              }
                            }}
                            disabled={uploadingHeader}
                            className="hidden"
                          />
                          {uploadingHeader ? (
                            <div className="flex items-center gap-2 text-gray-500">
                              <i className="ri-loader-4-line animate-spin"></i>
                              <span className="text-sm">アップロード中...</span>
                            </div>
                          ) : (
                            <>
                              <div className="w-8 h-8 flex items-center justify-center text-gray-400 mb-1">
                                <i className="ri-image-add-line text-2xl"></i>
                              </div>
                              <span className="text-sm text-gray-500">クリックして画像を選択</span>
                              <span className="text-xs text-gray-400 mt-1">記事詳細ページの上部に表示されます</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">本文</label>
                      <RichTextEditor
                        key={editorKey}
                        content={formData.content}
                        onChange={content => {
                          setFormData({ ...formData, content });
                          setFormData(prev => ({ ...prev, reading_time: calculateReadingTime(content) }));
                        }}
                        onImageUpload={handleContentImageUpload}
                        contentUploading={contentUploading}
                      />
                    </div>

                    {/* FAQ設定セクション */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-question-answer-line text-teal-600"></i>
                        FAQ（よくある質問）
                      </h3>
                      
                      <div className="space-y-4">
                        {formData.faqs.map((faq, index) => (
                          <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">質問 {index + 1}</span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => moveFAQ(index, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                                  title="上に移動"
                                >
                                  <i className="ri-arrow-up-line"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveFAQ(index, 'down')}
                                  disabled={index === formData.faqs.length - 1}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                                  title="下に移動"
                                >
                                  <i className="ri-arrow-down-line"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeFAQ(index)}
                                  className="p-1 text-red-400 hover:text-red-600 cursor-pointer"
                                  title="削除"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">質問</label>
                                <input
                                  type="text"
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                                  value={faq.question}
                                  onChange={e => updateFAQ(index, 'question', e.target.value)}
                                  placeholder="例：配信に必要な機材は何ですか？"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">回答</label>
                                <textarea
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                                  rows={3}
                                  value={faq.answer}
                                  onChange={e => updateFAQ(index, 'answer', e.target.value)}
                                  placeholder="例：基本的にはカメラ、マイク、配信ソフトが必要です。詳しくは..."
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <button
                          type="button"
                          onClick={addFAQ}
                          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-teal-500 hover:text-teal-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
                        >
                          <i className="ri-add-line"></i>
                          FAQを追加
                        </button>
                        
                        <p className="text-xs text-gray-500">
                          FAQはSEO対策として効果的です。記事に関連するよくある質問を追加してください。
                        </p>
                      </div>
                    </div>

                    {/* SEO設定セクション */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-seo-line text-teal-600"></i>
                        SEO設定
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            メタタイトル（SEO用タイトル）
                            <span className="text-gray-400 font-normal ml-2">60文字以内推奨</span>
                          </label>
                          <input
                            type="text"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                            value={formData.meta_title}
                            onChange={e => setFormData({ ...formData, meta_title: e.target.value })}
                            placeholder="検索結果に表示されるタイトル（空欄の場合は記事タイトルを使用）"
                            maxLength={70}
                          />
                          <p className="mt-1 text-xs text-gray-500">{formData.meta_title.length}/60文字</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            メタディスクリプション（検索結果の説明文）
                            <span className="text-gray-400 font-normal ml-2">120-160文字推奨</span>
                          </label>
                          <textarea
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                            rows={3}
                            value={formData.meta_description}
                            onChange={e => setFormData({ ...formData, meta_description: e.target.value })}
                            placeholder="検索結果に表示される説明文（空欄の場合は抜粋を使用）"
                            maxLength={200}
                          ></textarea>
                          <p className="mt-1 text-xs text-gray-500">{formData.meta_description.length}/160文字</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            キーワード
                            <span className="text-gray-400 font-normal ml-2">3-5個推奨</span>
                          </label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {formData.keywords.map((keyword, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                              >
                                {keyword}
                                <button
                                  type="button"
                                  onClick={() => removeKeyword(keyword)}
                                  className="hover:text-teal-900 cursor-pointer"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                              value={keywordInput}
                              onChange={e => setKeywordInput(e.target.value)}
                              onKeyDown={handleKeywordKeyDown}
                              placeholder="キーワードを入力してEnterで追加"
                              disabled={formData.keywords.length >= 10}
                            />
                            <button
                              type="button"
                              onClick={addKeyword}
                              disabled={!keywordInput.trim() || formData.keywords.length >= 10}
                              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                            >
                              追加
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{formData.keywords.length}/10個</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            カノニカルURL（重複コンテンツ対策）
                          </label>
                          <input
                            type="url"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                            value={formData.canonical_url}
                            onChange={e => setFormData({ ...formData, canonical_url: e.target.value })}
                            placeholder="https://example.com/blog/original-article"
                          />
                          <p className="mt-1 text-xs text-gray-500">他サイトから転載した記事の場合、元記事のURLを入力してください</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleSave('draft')}
                        disabled={saving}
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50"
                      >
                        下書き保存
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave('published')}
                        disabled={saving}
                        className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                      >
                        公開
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto">
                    {/* ヘッダー画像 */}
                    {formData.header_image && (
                      <div className="w-full h-96 mb-8 rounded-xl overflow-hidden">
                        <img 
                          src={formData.header_image} 
                          alt={formData.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* タイトルとメタ情報 */}
                    <div className="mb-8">
                      <h1 className="text-4xl font-bold text-gray-900 mb-4">{formData.title || '（タイトル未入力）'}</h1>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line"></i>
                          <span>{formatPreviewDate()}</span>
                        </div>
                        {formData.category && (
                          <div className="flex items-center gap-2">
                            <i className="ri-folder-line"></i>
                            <span>{formData.category}</span>
                          </div>
                        )}
                        {formData.reading_time > 0 && (
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line"></i>
                            <span>{formData.reading_time}分で読めます</span>
                          </div>
                        )}
                      </div>

                      {/* 著者情報 */}
                      {formData.author && (
                        <div className="flex items-center gap-3 py-4 border-y border-gray-200">
                          {formData.author_avatar && (
                            <img 
                              src={formData.author_avatar} 
                              alt={formData.author}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{formData.author}</p>
                            <p className="text-sm text-gray-500">著者</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 抜粋 */}
                    {formData.excerpt && (
                      <div className="bg-gray-50 border-l-4 border-teal-500 p-6 mb-8 rounded-r-lg">
                        <p className="text-lg text-gray-700 leading-relaxed">{formData.excerpt}</p>
                      </div>
                    )}

                    {/* 本文 */}
                    <div className="prose prose-lg max-w-none rich-editor">
                      <ContentRenderer content={formData.content || '<p>（本文未入力）</p>'} />
                    </div>

                    {/* FAQ */}
                    {formData.faqs && formData.faqs.length > 0 && (
                      <div className="mt-12 pt-8 border-t border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <i className="ri-question-line text-teal-600"></i>
                          よくある質問
                        </h2>
                        <div className="space-y-4">
                          {formData.faqs.map((faq, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-2">
                                <span className="text-teal-600 flex-shrink-0">Q.</span>
                                <span>{faq.question}</span>
                              </h3>
                              <div className="pl-6 text-gray-700 leading-relaxed">
                                <span className="text-teal-600 font-semibold">A. </span>
                                {faq.answer}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* キーワード */}
                    {formData.keywords && formData.keywords.length > 0 && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-500 mb-3">関連キーワード</h3>
                        <div className="flex flex-wrap gap-2">
                          {formData.keywords.map((keyword, index) => (
                            <span 
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                            >
                              #{keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filter & Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1 rounded cursor-pointer ${
                  statusFilter === 'all' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                全て ({posts.length})
              </button>
              <button
                onClick={() => setStatusFilter('published')}
                className={`px-3 py-1 rounded cursor-pointer ${
                  statusFilter === 'published' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                公開 ({publishedCount})
              </button>
              <button
                onClick={() => setStatusFilter('draft')}
                className={`px-3 py-1 rounded cursor-pointer ${
                  statusFilter === 'draft' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                下書き ({draftCount})
              </button>
            </div>
            {isSortMode && (
              <span className="text-sm text-teal-600 flex items-center gap-1">
                <i className="ri-information-line"></i>
                ドラッグして並び替えできます
              </span>
            )}
          </div>
        </div>

        {/* Posts List */}
        {loading ? (
          <p>読み込み中...</p>
        ) : isSortMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-4 py-3 w-10"></th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-700">タイトル</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-700">ステータス</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-700">作成日</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={filteredPosts.map(p => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredPosts.map(post => (
                    <SortableRow
                      key={post.id}
                      post={post}
                      onEdit={handleEdit}
                      onDuplicate={handleDuplicate}
                      onToggleStatus={handleToggleStatus}
                      onDelete={(id) => setDeleteConfirm(id)}
                      formatDate={formatPreviewDate}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        ) : (
          <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-3 text-sm font-medium text-gray-700">タイトル</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">ステータス</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">作成日</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map(post => (
                <tr key={post.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">{post.title}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      post.status === 'published' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {post.status === 'published' ? '公開中' : '下書き'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{formatPreviewDate(post.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleEdit(post)} className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer">
                        編集
                      </button>
                      <button onClick={() => handleDuplicate(post)} className="text-purple-600 hover:text-purple-800 text-sm cursor-pointer">
                        複製
                      </button>
                      <button onClick={() => handleToggleStatus(post)} className="text-green-600 hover:text-green-800 text-sm cursor-pointer">
                        {post.status === 'published' ? '下書きに戻す' : '公開'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(post.id)}
                        className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
              <p className="mb-4">本当に削除しますか？</p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  キャンセル
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  削除
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
